import { Stack, styled } from "@mui/material";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { Dispatch, useEffect, useState } from "react";
import { hasPermission } from "..";
import Permissions from "../../constants/permissions";
import useAuth, { DataGridColumns, DataGridPreferences, DataGrids } from "../../context/UserContext";
import { NewSearchTextBox } from "../../features/data-under-management/components/NewSearchTextBox";
import { CheckboxFilters } from "../../features/keys/components/CheckboxFilters";
import { useDebounce } from "../../lib/Hooks";
import { ExportFilter, SourceNode, SourceLeaf } from "../common/FilterPublishedArtifacts";
import { ClearThisFilter, FilterPanel, FilterRow, Item, SelectableItems } from "../Filters/Common";
import { DateRangeFilters } from "../Filters/DateRangeFilters";
import { ExportButton } from "../Filters/ExportButton";
import { ProjectsFilter } from "../Filters/ProjectsFilter";
import { PurposeFilters } from "../Filters/PurposeFilters";
import { IUploadsFilter } from "./uploadsDatagridUtils";
const StyledEye = styled(EyeIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));

const CHECKBOX_OPTIONS = [
  { value: DataGridColumns.ReportDateTime, label: "Report date" },
  { value: DataGridColumns.Created, label: "Created" },
  { value: DataGridColumns.Started, label: "Started" },
  { value: DataGridColumns.Finished, label: "Finished" },
  { value: DataGridColumns.Project, label: "Project name" },
  { value: DataGridColumns.Name, label: "Name" },
  { value: DataGridColumns.UploadDuration, label: "Upload duration" },
  { value: DataGridColumns.QueuedDuration, label: "Queued duration" },
  { value: DataGridColumns.Purpose, label: "Purpose" },
  { value: DataGridColumns.Destination, label: "Destination" },
  { value: DataGridColumns.Status, label: "Status" },
  { value: DataGridColumns.Actions, label: "Actions" },
];

const COLUMNS = [
  DataGridColumns.ReportDateTime,
  DataGridColumns.Created,
  DataGridColumns.Started,
  DataGridColumns.Finished,
  DataGridColumns.Project,
  DataGridColumns.Name,
  DataGridColumns.UploadDuration,
  DataGridColumns.QueuedDuration,
  DataGridColumns.Purpose,
  DataGridColumns.Destination,
  DataGridColumns.Status,
  DataGridColumns.Actions,
];

const StatusFilters = ({
  selected,
  onClick,
  onClearThisFilter,
}: {
  selected: string;
  onClick: (purpose: string) => void;
  onClearThisFilter: () => void;
}) => {
  return (
    <Stack sx={{ gap: "12px" }}>
      <FilterRow value="completed" onClick={onClick} text="Completed" isSelected={selected === "completed"} />
      <FilterRow value="failed" onClick={onClick} text="Failed" isSelected={selected === "failed"} />
      <FilterRow value="unfinished" onClick={onClick} text="Unfinished" isSelected={selected === "unfinished"} />
      <ClearThisFilter onClick={onClearThisFilter} />
    </Stack>
  );
};

const FilterItems = ({
  selectedItem,
  setSelectedItem,
  filter,
}: {
  selectedItem: SelectableItems;
  setSelectedItem: Dispatch<React.SetStateAction<SelectableItems>>;
  filter: IUploadsFilter;
}) => {
  return (
    <>
      <Item
        text="Report date"
        isSelected={selectedItem === "reportDate"}
        onClick={() => setSelectedItem("reportDate")}
        hasFiltersApplied={filter.reportRange.start && filter.reportRange.end ? true : false}
      />
      <Item
        text="Started"
        isSelected={selectedItem === "startedDate"}
        onClick={() => setSelectedItem("startedDate")}
        hasFiltersApplied={filter.uploadedRange.start && filter.uploadedRange.end ? true : false}
      />
      <Item
        text="Name"
        isSelected={selectedItem === "name"}
        onClick={() => setSelectedItem("name")}
        hasFiltersApplied={!!filter.projects?.length || !!filter.exportNames.length}
      />
      <Item
        text="Purpose"
        isSelected={selectedItem === "purpose"}
        onClick={() => setSelectedItem("purpose")}
        hasFiltersApplied={filter.purpose ? true : false}
      />
      <Item
        text="Status"
        isSelected={selectedItem === "status"}
        onClick={() => setSelectedItem("status")}
        hasFiltersApplied={filter.status ? true : false}
      />
    </>
  );
};

type RightPanelProps = {
  filter: IUploadsFilter;
  setFilter: Dispatch<React.SetStateAction<IUploadsFilter>>;
  selectedItem: SelectableItems;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
};

const RightPanel = ({
  filter,
  setFilter,
  selectedItem,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
}: RightPanelProps) => {
  const handleSelectCallback = (nodes: SourceLeaf[], takeOut?: boolean) => {
    setFilter((prevState) => {
      const newFilterValue = {
        exportNames: new Set(prevState.exportNames),
        projects: new Set(prevState.projects),
      };

      nodes.forEach((node) => {
        const nodeFilterValue = node.filterValue as ExportFilter;
        if (!nodeFilterValue) {
          return;
        }

        if (takeOut) {
          if (nodeFilterValue.exportName) {
            newFilterValue.exportNames.delete(nodeFilterValue.exportName);
          }
          if (nodeFilterValue.project) {
            newFilterValue.projects.delete(nodeFilterValue.project);
          }
        } else {
          if (nodeFilterValue.exportName) {
            newFilterValue.exportNames.add(nodeFilterValue.exportName);
          }

          if (nodeFilterValue.project) {
            newFilterValue.projects.add(nodeFilterValue.project);
          }
        }
      });

      return {
        ...prevState,
        exportNames: Array.from(newFilterValue.exportNames),
        projects: Array.from(newFilterValue.projects),
      };
    });
  };

  if (selectedItem === "reportDate") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, reportRange: { start, end } })}
        start={filter.reportRange.start || null}
        end={filter.reportRange.end || null}
        onClearThisFilter={() => setFilter({ ...filter, reportRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "startedDate") {
    return (
      <DateRangeFilters
        onSelectDate={({ start, end }) => setFilter({ ...filter, uploadedRange: { start, end } })}
        start={filter.uploadedRange.start || null}
        end={filter.uploadedRange.end || null}
        onClearThisFilter={() => setFilter({ ...filter, uploadedRange: { start: null, end: null } })}
      />
    );
  }

  if (selectedItem === "name") {
    return (
      <ProjectsFilter
        selectedNodesInFilter={selectedNodesInFilter}
        nonSelectedNodesInFilter={nonSelectedNodesInFilter}
        onClearThisFilter={() => setFilter({ ...filter, projects: [], exportNames: [] })}
        onSelectCallback={handleSelectCallback}
      />
    );
  }

  if (selectedItem === "purpose") {
    return (
      <PurposeFilters
        selected={filter.purpose}
        onClick={(purpose) => setFilter({ ...filter, purpose: filter.purpose === purpose ? "" : purpose })}
        onClearThisFilter={() =>
          setFilter({
            ...filter,
            purpose: "",
          })
        }
      />
    );
  }

  if (selectedItem === "status") {
    return (
      <StatusFilters
        selected={filter.status}
        onClick={(status) => setFilter({ ...filter, status: filter.status === status ? "" : status })}
        onClearThisFilter={() =>
          setFilter({
            ...filter,
            status: "",
          })
        }
      />
    );
  }

  return null;
};

const handleClearAllFilters = (setFilter: Dispatch<React.SetStateAction<IUploadsFilter>>) => {
  setFilter({
    projects: [],
    exportNames: [],
    purpose: "",
    search: "",
    status: "",
    reportRange: {
      start: null,
      end: null,
    },
    uploadedRange: {
      start: null,
      end: null,
    },
    sortBy: undefined,
    sortDirection: undefined,
  });
};

const isFilteringUploads = (filter: IUploadsFilter) =>
  Boolean(filter.reportRange.start) ||
  Boolean(filter.reportRange.end) ||
  Boolean(filter.uploadedRange.start) ||
  Boolean(filter.uploadedRange.end) ||
  Boolean(filter.projects.length) ||
  Boolean(filter.exportNames.length) ||
  Boolean(filter.purpose) ||
  Boolean(filter.search) ||
  Boolean(filter.status);

type UploadFiltersProps = {
  filter: IUploadsFilter;
  setFilter: Dispatch<React.SetStateAction<IUploadsFilter>>;
  queryVariables: UploadsFilters;
  selectedNodesInFilter: SourceNode[];
  nonSelectedNodesInFilter: SourceNode[];
  loading: boolean;
  datagridPreferencesElement: DataGridPreferences;
  preferenceElementToUpdate: DataGrids;
  initialVisibilityModel: { [key in DataGridColumns]?: boolean };
  hideTextSearch?: boolean;
};

const UploadFilters = ({
  filter,
  setFilter,
  queryVariables,
  selectedNodesInFilter,
  nonSelectedNodesInFilter,
  loading,
  datagridPreferencesElement,
  preferenceElementToUpdate,
  initialVisibilityModel,
  hideTextSearch,
}: UploadFiltersProps) => {
  const { updateDataGridPreferences, user } = useAuth();

  const [searchValue, setSearchValue] = useState(filter.search || "");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [selectedItem, setSelectedItem] = useState<SelectableItems>("reportDate");

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      search: debouncedSearchValue,
    }));
  }, [debouncedSearchValue, setFilter]);

  const getSelectedOptions = () => {
    const columnVisibilityModel = datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel;
    return COLUMNS.filter((col) => columnVisibilityModel?.[col]);
  };

  const columnsWidth = datagridPreferencesElement?.columnsWidth;
  const showClearAllFilters = isFilteringUploads(filter);

  return (
    <Stack direction="row" sx={{ gap: "12px", alignItems: "center", height: "48px" }}>
      {hideTextSearch ? null : (
        <Stack sx={{ width: "300px" }}>
          <NewSearchTextBox value={searchValue} onChange={setSearchValue} placeholder="Search" />
        </Stack>
      )}
      <FilterPanel
        loading={loading}
        biggerWidth={selectedItem === "name"}
        showClearAllFilters={showClearAllFilters}
        filterCount={showClearAllFilters ? 1 : 0}
        onClearAllFilters={() => handleClearAllFilters(setFilter)}
        rightPanel={
          <RightPanel
            filter={filter}
            setFilter={setFilter}
            selectedItem={selectedItem}
            selectedNodesInFilter={selectedNodesInFilter}
            nonSelectedNodesInFilter={nonSelectedNodesInFilter}
          />
        }
        filterItems={<FilterItems selectedItem={selectedItem} setSelectedItem={setSelectedItem} filter={filter} />}
      />
      <CheckboxFilters
        Icon={<StyledEye size={24} />}
        options={CHECKBOX_OPTIONS.filter((option) => {
          if (option.value === DataGridColumns.Actions && !hasPermission(Permissions.ManageAccountSettings, user))
            return false;
          return true;
        })}
        onClickOption={(value) => {
          const visibilityModel = datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel;
          updateDataGridPreferences(preferenceElementToUpdate, {
            columnsOrder: datagridPreferencesElement?.columnsOrder,
            columnsWidth: columnsWidth,
            columnVisibilityModel: {
              ...visibilityModel,
              [value]: !visibilityModel[value],
            },
          });
        }}
        selectedOptions={getSelectedOptions()}
      />
      <ExportButton queryVariables={queryVariables} query="uploads" />
    </Stack>
  );
};

export { isFilteringUploads, CHECKBOX_OPTIONS as UPLOAD_CHECKBOX_OPTIONS, UploadFilters };
